import React from 'react';
import { Upload, FileText, Image, Video, Award, File, CheckSquare, Square, FileSpreadsheet, Presentation, Trophy, GraduationCap, Code, Newspaper, BookOpen, BarChart, Download, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DEMO_ABOUT_TEXT, DEMO_SUBTITLE, DEMO_FOOTER_TEXT, DEMO_EXAMPLE_FILE } from '../config/demoData';

const PortfolioFeatureDemo = ({ 
  aboutText,
  subtitle,
  footerText,
  exampleFile: customExampleFile,
  hideAboutSection = false
}) => {
  const categories = [
    { id: 'word', label: 'Word Documents', icon: FileText, color: 'blue' },
    { id: 'excel', label: 'Excel Spreadsheets', icon: FileSpreadsheet, color: 'green' },
    { id: 'powerpoint', label: 'PowerPoint Presentations', icon: Presentation, color: 'orange' },
    { id: 'images', label: 'Pictures/Images', icon: Image, color: 'purple' },
    { id: 'videos', label: 'Videos', icon: Video, color: 'red' },
    { id: 'awards', label: 'Awards', icon: Trophy, color: 'yellow' },
    { id: 'certificates', label: 'Certificates', icon: GraduationCap, color: 'indigo' },
    { id: 'projects', label: 'Projects', icon: Code, color: 'pink' },
    { id: 'publications', label: 'Publications', icon: Newspaper, color: 'teal' },
    { id: 'training', label: 'Training Materials', icon: BookOpen, color: 'cyan' },
    { id: 'reports', label: 'Reports & Analysis', icon: BarChart, color: 'amber' },
    { id: 'other', label: 'Other', icon: File, color: 'gray' }
  ];

  // Use props if provided, otherwise use defaults from config
  const about = aboutText !== undefined ? aboutText : DEMO_ABOUT_TEXT;
  const headerSubtitle = subtitle !== undefined ? subtitle : DEMO_SUBTITLE;
  const footer = footerText !== undefined ? footerText : DEMO_FOOTER_TEXT;
  const exampleFile = customExampleFile !== undefined ? customExampleFile : DEMO_EXAMPLE_FILE;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const IconComponent = Presentation;

  return (
    <div className="space-y-6 -mx-24 lg:-mx-40 xl:-mx-56 2xl:-mx-64">
      {/* About Section - Outside the Example Portfolio Showcase (hidden if hideAboutSection is true) */}
      {!hideAboutSection && (
        <div className="card mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About</h2>
          <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {about}
          </div>
        </div>
      )}

      {/* Example Portfolio Showcase */}
      <div className="card border-2 border-primary-200 bg-gradient-to-br from-white to-primary-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white p-6 rounded-t-lg mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold mb-2">Example Portfolio Showcase</h2>
              <p className="text-primary-100 text-lg">{headerSubtitle}</p>
            </div>
            <div className="bg-white/20 px-4 py-2 rounded-lg">
              <span className="text-sm font-semibold">EXAMPLE PORTFOLIO</span>
            </div>
          </div>
        </div>

        {/* Portfolio File Display */}
        <div className="px-6 pb-6">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Presentation className="w-6 h-6 text-primary-600" />
              PowerPoint Presentations (1)
            </h3>
            
            <div className="bg-white border-2 border-primary-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <IconComponent className="w-8 h-8 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">
                      {exampleFile.name}
                    </h4>
                    <p className="text-sm text-gray-500 mb-3">
                      PowerPoint Presentation • {formatFileSize(exampleFile.size)} • Uploaded {new Date(exampleFile.uploadedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">Summary (AI-Generated):</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{exampleFile.summary}</p>
                    </div>
                  </div>
                </div>
                <a
                  href={exampleFile.url}
                  download
                  className="ml-4 p-3 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                  title="Download file"
                >
                  <Download className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>


          {/* Category Selection Demo */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-primary-600" />
              Available Portfolio Categories
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Select one or more categories to organize your files. Files will be automatically sorted into selected categories.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {categories.map(category => {
                const CategoryIcon = category.icon;
                const isSelected = category.id === 'powerpoint'; // Highlight PowerPoint as selected
                return (
                  <div
                    key={category.id}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {isSelected ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    <CategoryIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">{category.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Drag and Drop Area Demo */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary-600" />
              Try It Yourself - Drag & Drop Interface
            </h4>
            <div className="border-2 border-dashed border-primary-400 rounded-lg p-8 text-center bg-gradient-to-br from-primary-50 to-blue-50">
              <Upload className="w-16 h-16 text-primary-500 mx-auto mb-4" />
              <p className="text-xl font-medium text-gray-700 mb-2">
                Drag and drop files here, or click to browse
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Files will be automatically organized into selected categories
              </p>
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium shadow-lg cursor-pointer hover:bg-primary-700 transition-colors">
                <Upload className="w-5 h-5" />
                <span>Select Files</span>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600" />
                Automatic File Analysis
              </h5>
              <p className="text-sm text-gray-600">
                Word, Excel, and PowerPoint files are automatically analyzed to generate 1-2 paragraph summaries
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Award className="w-5 h-5 text-primary-600" />
                Smart Organization
              </h5>
              <p className="text-sm text-gray-600">
                Files are automatically categorized and organized by type. Drag files between categories to reorganize
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Image className="w-5 h-5 text-primary-600" />
                Multiple File Types
              </h5>
              <p className="text-sm text-gray-600">
                Support for documents, images, videos, certificates, awards, and more
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Code className="w-5 h-5 text-primary-600" />
                Edit Summaries
              </h5>
              <p className="text-sm text-gray-600">
                Review and customize AI-generated summaries for your Office documents
              </p>
            </div>
          </div>

          {/* Call to Action banner removed per user request */}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 px-6 py-6 rounded-b-lg border-t border-gray-200">
          {/* How This Portfolio Was Created */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary-600" />
              How This Portfolio Was Created
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col items-center text-center">
                <div className="bg-primary-100 rounded-full p-3 mb-3">
                  <span className="text-primary-700 font-bold text-lg">1</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-1">Select Categories</h5>
                <p className="text-sm text-gray-600">
                  The user selected "PowerPoint Presentations" from the category checkboxes
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-primary-100 rounded-full p-3 mb-3">
                  <span className="text-primary-700 font-bold text-lg">2</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-1">Drag & Drop</h5>
                <p className="text-sm text-gray-600">
                  The PowerPoint file was dragged and dropped into the upload area
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-primary-100 rounded-full p-3 mb-3">
                  <span className="text-primary-700 font-bold text-lg">3</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-1">Automatic Analysis</h5>
                <p className="text-sm text-gray-600">
                  The system automatically analyzed the file and generated a professional summary
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-primary-100 rounded-full p-3 mb-3">
                  <span className="text-primary-700 font-bold text-lg">4</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-1">Organization</h5>
                <p className="text-sm text-gray-600">
                  The file was automatically organized into the PowerPoint Presentations category
                </p>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="text-sm text-gray-600 text-center">
            <div className="font-semibold text-gray-900">Career Branding Network</div>
            <div>Example Portfolio - {new Date().getFullYear()}</div>
            <div className="text-xs mt-2">{footer}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioFeatureDemo;
